// client/src/pages/Game/Battlefield/components/MonsterZone.jsx

import React from 'react';
import './PendingAction.css';

/**
 * Total des bonus d'aura (ATK/DEF) reçus par une carte — sert à signaler
 * visuellement les stats boostées (ex. Sylvestus : +2 DEF aux alliés TERRE,
 * terrain, équipements…) sans avoir à ouvrir l'aperçu de la carte source.
 */
const auraBuffTotal = (card, type) =>
  (card.auraBuffs || []).filter(b => b.type === type).reduce((s, b) => s + b.value, 0);

/* R34i : modificateurs d'ATK temporaires (buffs + debuffs) — la valeur
 * affichée est l'ATK EFFECTIVE, rouge (▼) si réduite, verte (▲) si boostée. */
const atkModifierTotal = (card) =>
  [].concat(card.buffs || [], card.debuffs || [])
    .filter(b => b.type === 'ATK')
    .reduce((s, b) => s + b.value, 0);

const auraBuffTitle = (card, type) => {
  const buffs = (card.auraBuffs || []).filter(b => b.type === type);
  if (buffs.length === 0) return undefined;
  return buffs
    .map(b => `${b.value >= 0 ? '+' : ''}${b.value} — aura : ${b.sourceName || 'source inconnue'}`)
    .join(' · ');
};

/**
 * Zone de monstres. Position et taille en pourcentages du frame.
 */
const MonsterZone = ({
  player,
  gameState,
  position,
  size,
  onCardClick,
  onCardDragStart,
  onZoneClick,
  onDevDragStart,
  devMode,
  getCardElement,
  abilityTargeting
}) => {
  // Animation d'invocation : detecte les monstres apparus depuis le rendu precedent
  const prevIdsRef = React.useRef(new Set());
  // Set persistant (croissant) : si un etat intermediaire vide arrive (effet
  // adverse, refresh), les cartes connues ne sont PAS reanimees a leur retour.
  const seenIdsRef = React.useRef(new Set());
  const currentIds = new Set();
  // Enregistre les ids affiches pour le rendu suivant (apres paint)
  React.useEffect(() => {
    currentIds.forEach((id) => seenIdsRef.current.add(id));
    prevIdsRef.current = currentIds;
  });

  if (!gameState) return null;

  const monsters = player === 'player'
    ? gameState.playerField.filter(card => card.zone === 'monster')
    : gameState.opponentField.filter(card => card.zone === 'monster');

  const isPlayerMonsterZone = player === 'player';
  const isPlayerTurn = gameState.activePlayer === 'player';
  // Mode défense : l'adversaire attaque — on doit pouvoir dragger ses monstres
  // pour les envoyer défendre, même si ce n'est pas formellement notre tour.
  const isDefenseMode = gameState.opponentHasAttack &&
    gameState.pendingAttack &&
    gameState.pendingAttack.attackers &&
    gameState.pendingAttack.attackers.length > 0;

  // R52 — Combattant : quand on a le jeton d'attaque et qu'un de nos
  // attaquants FIGHTER en zone de combat n'a pas encore de défenseur, on peut
  // dragger un monstre ADVERSE pour le FORCER à défendre (FORCE_ENGAGE).
  const pendingDefenders = (gameState.pendingAttack && gameState.pendingAttack.defenders) || [];
  const readyFighters = (gameState.playerField || []).filter(c =>
    c.zone === 'battle' && c.role === 'attacker' &&
    (c.abilities || []).includes('FIGHTER') &&
    !pendingDefenders.some(d => d.blocksInstanceId === c.instanceId));
  const fighterEngageMode = !isPlayerMonsterZone &&
    gameState.playerHasAttack && readyFighters.length > 0;

  const canDragField = isPlayerMonsterZone
    ? (isPlayerTurn || isDefenseMode)
    : fighterEngageMode;
  // Le drop reste autorisé en mode dev — le drag de cartes est du gameplay
  const canDrop = isPlayerMonsterZone && isPlayerTurn;
  const shouldGlow = isPlayerMonsterZone && isPlayerTurn && monsters.length === 0;

  const isPendingAction = (monster) => {
    if (!gameState.pendingAction) return false;
    if (gameState.pendingAction.type === 'SUMMON' &&
        player === gameState.activePlayer &&
        gameState.pendingAction.position === monster.position) {
      return 'pending-action';
    }
    if (gameState.pendingAction.type === 'PREPARE_ATTACK' &&
        player === gameState.activePlayer &&
        gameState.pendingAction.position === monster.position) {
      return 'pending-attack';
    }
    if (gameState.pendingAction.type === 'PREPARE_DEFENSE' &&
        player === gameState.activePlayer &&
        gameState.pendingAction.position === monster.position) {
      return 'pending-defense';
    }
    return false;
  };

  // Capacite a activation manuelle (trigger MANUAL / ONCE_PER_TURN / type ACTIVE)
  // → brillance dorée sur la carte pendant notre tour.
  const hasActivatableEffect = (monster) =>
    (monster.effects || []).some(eff =>
      eff && (eff.trigger === 'MANUAL' || eff.trigger === 'ONCE_PER_TURN' || eff.type === 'ACTIVE')
    );

  const handleZoneClick = (e) => {
    if (onZoneClick) onZoneClick(e, 'monster');
  };

  const handleDevDrag = (e) => {
    if (devMode && onDevDragStart) onDevDragStart(e);
  };

  // Ordonner les monstres par position (FIELD_SIZE_LIMIT serveur = 6)
  const FIELD_SLOTS = 6;
  const getOrderedMonsters = () => {
    const orderedSlots = Array(FIELD_SLOTS).fill(null);
    monsters.forEach(monster => {
      if (monster.position >= 0 && monster.position < FIELD_SLOTS) {
        orderedSlots[monster.position] = monster;
      }
    });
    return orderedSlots.filter(m => m !== null);
  };

  const orderedMonsters = getOrderedMonsters();
  return (
    <div
      className={`game-element monsters-zone ${player}-monsters ${canDrop ? 'droppable' : ''} ${shouldGlow ? 'empty-zone-glow' : ''}`}
      style={{
        left: `${position.x}%`,
        top: `${position.y}%`,
        width: `${size.width}cqw`,
        height: `${size.height}cqw`,
        transform: 'translate(-50%, -50%)',
        cursor: devMode ? 'move' : (canDrop ? 'pointer' : 'default'),
        borderRadius: '8px',
        border: shouldGlow ? '2px dashed rgba(255, 255, 255, 0.7)' : 'none',
        boxShadow: shouldGlow ? '0 0 15px rgba(255, 255, 255, 0.5)' : 'none',
        animation: shouldGlow ? 'empty-zone-pulse 1.5s infinite alternate' : 'none',
      }}
      onMouseDown={devMode ? handleDevDrag : undefined}
      onTouchStart={devMode ? handleDevDrag : undefined}
      onClick={!devMode ? handleZoneClick : undefined}
    >
      <div className="monster-container" style={{
        display: 'flex',
        height: '100%',
        width: '100%',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          gap: '0.8cqw',
          height: '100%',
        }}>
          {orderedMonsters.map((monster) => {
            const pendingActionClass = isPendingAction(monster);
            const monsterId = monster.instanceId || monster.id;
            const isJustSummoned = prevIdsRef.current.size > 0
              && !seenIdsRef.current.has(monsterId)
              && !prevIdsRef.current.has(monsterId);
            currentIds.add(monsterId);
            return (
              <div
                key={`${player}-monster-${monster.position}`}
                className={`monster-card ${isJustSummoned ? 'just-summoned' : ''} ${monster.isToken ? 'is-token' : ''} ${isPlayerMonsterZone && isPlayerTurn && !monster.hasAttacked ? 'can-attack' : ''} ${pendingActionClass || ''} ${isPlayerMonsterZone && isPlayerTurn && hasActivatableEffect(monster) ? 'has-activatable' : ''} ${abilityTargeting && monster.zone === 'monster' ? 'ability-targetable' : ''} ${fighterEngageMode ? 'fighter-targetable' : ''} ${monster.locked ? 'fighter-locked' : ''} ${(monster.abilities || []).includes('FIGHTER') ? 'has-fighter' : ''}`}
                data-position={monster.position}
                data-instance-id={monster.instanceId || monster.id}
                style={{
                  height: '100%',
                  aspectRatio: '2 / 3',
                }}
                onMouseDown={(e) => {
                  // Priorité gameplay : drag de monstre actif même en mode dev
                  if (canDragField && onCardDragStart) {
                    onCardDragStart(e, { ...monster, location: 'field' });
                  }
                  e.stopPropagation();
                }}
                onTouchStart={(e) => {
                  if (canDragField && onCardDragStart) {
                    onCardDragStart(e, { ...monster, location: 'field' });
                  }
                  e.stopPropagation();
                }}
                onClick={(e) => {
                  if (onCardClick) {
                    onCardClick(monster);
                    e.stopPropagation();
                  }
                }}
              >
                <img
                  src={monster.imageUrl || `/api/static/images/cards/${getCardElement(monster)}.svg`}
                  alt={monster.name || 'Monster'}
                  onError={(e) => {
                    e.target.src = `/api/static/images/card-backs/neutral.webp`;
                  }}
                />
                <div className="monster-stats">
                  <span
                    className={`attack ${(atkModifierTotal(monster) + auraBuffTotal(monster, 'ATK')) < 0 ? 'stat-lowered' : ''} ${(atkModifierTotal(monster) + auraBuffTotal(monster, 'ATK')) > 0 ? 'stat-buffed' : ''}`}
                    title={[
                      auraBuffTitle(monster, 'ATK'),
                      atkModifierTotal(monster) !== 0 ? `Modificateurs ATK : ${atkModifierTotal(monster) > 0 ? '+' : ''}${atkModifierTotal(monster)}` : undefined,
                    ].filter(Boolean).join(' — ')}
                  >
                    <span className="stat-icon">⚔</span>
                    {/* R34gg : monster.attack est l'ATK EFFECTIVE serveur (aura/buffs
                        déjà appliqués) — ne PAS re-ajouter les modificateurs. */}
                    {Math.max(0, monster.attack || 0)}
                  </span>
                  {/* BUGFIX (2026-08-18) : afficher les PV RÉELS (currentHP) et non
                      la stat de base (defense) — les dégâts étaient invisibles. */}
                  <span
                    className={`defense ${(monster.currentHP ?? monster.maxHP ?? monster.defense) < (monster.maxHP ?? monster.defense) ? 'damaged' : ''} ${auraBuffTotal(monster, 'DEF') > 0 ? 'stat-buffed' : ''}`}
                    title={[
                      auraBuffTitle(monster, 'DEF'),
                      `PV : ${monster.currentHP ?? '?'} / ${monster.maxHP ?? '?'}`,
                    ].filter(Boolean).join(' — ')}
                  >
                    <span className="stat-icon">❤</span>
                    {(monster.currentHP ?? monster.defense) || 0}
                  </span>
                </div>
                {isPlayerMonsterZone && isPlayerTurn && !monster.hasAttacked && (
                  <div className="attack-indicator">
                    {gameState.playerHasAttack ? "⚔️" : "🛡️"}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default MonsterZone;
